import { Injectable } from '@angular/core';
import { DataService } from './data.service';
import { Subject } from 'rxjs';
import { DataChangeEmitPayload, DataChangeEmitWhat, DataChangeEmitWho } from '../models/data-change-emit/data-change-emit-payload';
import { TimeManagerProject } from '../entities/TimeManagerProject';
import { InFilter } from '../baseServices/filter.interface';
import { GlobalConstants } from '../constant/constant';
import { StoreName } from '../entities/StoreName';
import { DbTransaction } from '../models/db/db-transaction';
import { Callback, CallbackNames } from '../models/db/db-transaction-callback';
import { EntityHelper, Check } from '../utils/entity-helper';
import { RecordNotFoundException, RecordSoftDeletedException } from '../utils/exceptions';

@Injectable({
  providedIn: 'root',
})
export class TimeManagerProjectService extends DataService {
  private emitChangeSource = new Subject<DataChangeEmitPayload<TimeManagerProject>>();
  changeEmitted$ = this.emitChangeSource.asObservable();

  constructor() {
    super();
  }

  // #region Utility

  emitChange(timeManagerProject: TimeManagerProject, who: DataChangeEmitWho, what: DataChangeEmitWhat) {
    this.emitChangeSource.next({ data: timeManagerProject, who: who, what: what });
  }

  getEmitChangeCallback(timeManagerProject: TimeManagerProject, who: DataChangeEmitWho, what: DataChangeEmitWhat): Callback {
    return {
      name: CallbackNames.EmitChangeForTimeManagerProject + '_' + timeManagerProject.RecordId,
      fn: this.emitChange.bind(this, timeManagerProject, who, what),
    };
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.TimeManager2Project], 'readwrite'));
  }

  // #endregion
  // #region Low-level Get One

  async getTimeManagerProjectByOnlineId(onlineId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeManagerProject> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByOnlineId<TimeManagerProject>(StoreName.TimeManager2Project, onlineId, TimeManagerProject, transaction);

    if (result === null) {
      throw new RecordNotFoundException(onlineId);
    }

    const record = result as TimeManagerProject;

    if (this.checkIsTimeManagerProjectSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(onlineId);
    }

    return record;
  }

  async getTimeManagerProjectByRecordId(recordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeManagerProject> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByLocalId<TimeManagerProject>(StoreName.TimeManager2Project, recordId, TimeManagerProject, transaction);

    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as TimeManagerProject;

    if (this.checkIsTimeManagerProjectSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(recordId);
    }

    return record;
  }

  // #endregion
  // #region Low-level Get Many

  async getAllTimeManagerProjects(filter: any = undefined, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeManagerProject[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAll<TimeManagerProject>(StoreName.TimeManager2Project, filter, TimeManagerProject, transaction);

    let records = result as TimeManagerProject[];

    if (!includeDeleted) {
      records = records.filter(timeManagerProject => !this.checkIsTimeManagerProjectSoftDeleted(timeManagerProject));
    }

    return records;
  }

  async getTimeManagerProjectsByOnlineIds(onlineIds: number[], existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeManagerProject[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const filter: InFilter = { in: { field: 'Id', searchArray: onlineIds } };

    const result = await this.repository.getAll<TimeManagerProject>(StoreName.TimeManager2Project, filter, TimeManagerProject, transaction);

    let records = result as TimeManagerProject[];

    if (!includeDeleted) {
      records = result.filter(timeManagerProject => !this.checkIsTimeManagerProjectSoftDeleted(timeManagerProject));
    }

    return records;
  }

  // #endregion
  // region Low-level Create

  private async createTimeManagerProject(who: DataChangeEmitWho, recordToCreate: TimeManagerProject, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LC1. Validate action
    await this.validateTimeManagerProjectCreation(transaction);

    // TODO: Impelment LC2 and LC3

    // LC4. Repository call
    const recordId = await this.repository.create(recordToCreate, StoreName.TimeManager2Project, transaction);

    // LC5. Get created record
    const createdRecord = await this.getTimeManagerProjectByRecordId(recordId, transaction, includeDeleted);

    // LC6. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(createdRecord, who, DataChangeEmitWhat.Create));

    // LC7. Return id
    return recordId;
  }

  // #endregion
  // region Low-level Update

  async updateTimeManagerProject(
    who: DataChangeEmitWho,
    recordToUpdate: TimeManagerProject,
    skipLocalCopyValidation: boolean,
    localCopy: TimeManagerProject | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LU1. Get existing record
    const existingRecord = await this.getTimeManagerProjectByRecordId(recordToUpdate.RecordId, transaction);

    // LU2. Validate action
    await this.validateTimeManagerProjectUpdate(recordToUpdate, transaction);

    // LU3. Idempotent check
    if (EntityHelper.checkAllFieldsEqual(existingRecord, recordToUpdate)) {
      return;
    }

    // LU4. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<TimeManagerProject>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy<TimeManagerProject>(localCopy, existingRecord, checks);
    }

    // LU5. Validate model
    await this.validateTimeManagerProject(recordToUpdate, transaction);

    // LU6. Repository call
    await this.repository.update(recordToUpdate, StoreName.TimeManager2Project, transaction);

    // LU7. Get updated record
    const updatedRecord = await this.getTimeManagerProjectByRecordId(recordToUpdate.RecordId, transaction);

    // LU8. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(updatedRecord, who, DataChangeEmitWhat.Update));

    // LU9. Return
    return;
  }

  // #endregion
  // region Low-level Delete

  // #endregion
  // region Low-level Validate Model

  async validateTimeManagerProject(timeManagerProject: TimeManagerProject, existingTransaction?: DbTransaction): Promise<void> {
    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Low-level Validate Action

  async validateTimeManagerProjectCreation(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  async validateTimeManagerProjectUpdate(timeManagerProject: TimeManagerProject, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Mid-level Get Many

  async getTimeManagerProjectsByTeamId(teamId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeManagerProject[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<TimeManagerProject>(StoreName.TimeManager2Project, GlobalConstants.KEY_NAMES.TeamId, teamId, TimeManagerProject, transaction);

    let records = result as TimeManagerProject[];

    if (!includeDeleted) {
      records = records.filter(timeManagerProject => !this.checkIsTimeManagerProjectSoftDeleted(timeManagerProject));
    }

    return records;
  }
  
  async getTimeManagerProjectsByUserId(userId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeManagerProject[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<TimeManagerProject>(StoreName.TimeManager2Project, GlobalConstants.KEY_NAMES.UserId, userId, TimeManagerProject, transaction);

    let records = result as TimeManagerProject[];

    if (!includeDeleted) {
      records = records.filter(timeManagerProject => !this.checkIsTimeManagerProjectSoftDeleted(timeManagerProject));
    }

    return records;
  }

  // #endregion
  // region Mid-level Create

  // #endregion
  // region Mid-level Update

  // #endregion
  // region Mid-level Delete

  // #endregion
  // region Mid-level Validate

  // #endregion
  // region Mid-level Upsert

  // #endregion
  // region Mid-level Sync

  async upsertTimeManagerProjectFromSync(who: DataChangeEmitWho, recordToUpsert: TimeManagerProject, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    try {
      const existingRecord = await this.getTimeManagerProjectByOnlineId(recordToUpsert.Id, transaction, true);
      recordToUpsert.RecordId = existingRecord.RecordId;
      await this.updateTimeManagerProject(who, recordToUpsert, true, null, transaction);
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        await this.createTimeManagerProject(who, recordToUpsert, transaction, true);
      }
    }

    return;
  }

  // #endregion
  // region Check

  private checkIsTimeManagerProjectSoftDeleted(record: TimeManagerProject): boolean {
    return record.IsDeleted;
  }

  private checkImmutableFieldsMatch(a: TimeManagerProject, b: TimeManagerProject): boolean {
    return a.ManagerId === b.ManagerId && a.ProjectId === b.ProjectId;
  }

  // #endregion
  // region Lookup

  // #endregion
  // region Transform

  // #endregion
  // region Other

  // #endregion
}
